import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import {
  NO_USER_CHOICE,
  PREVIEW_TABLE_KEY,
  PREVIEW_FIELD_NAME,
  chooseResequencePreview,
  resequencePreviewPreferenceBody,
  resolveResequencePreview,
  savedResequencePreviewChoice
} from './resequencePreviewStartingState'

/**
 * The resequencing preview switch's preference (contract §6.1, §6.4): the user's saved choice wins; with none,
 * the switch follows the plant value `frontEndConfig.plannerResequencePreviewDefault` (even when the configuration
 * arrives later); with none of them, on. The plant value is never written as the user's choice: only a click saves.
 * Reading never retries nor warns, and a failed save keeps the choice for the session without a notice (POL4).
 *
 * @param {Object} options
 * @param {Boolean} options.enabled Reads it (false: a plan with nothing to show it on asks nothing)
 * @returns {[Boolean, Function]} Whether the preview is on, and the function that flips it
 */
const useResequencePreviewPreference = ({ enabled = true } = {}) => {
  const [frontEndConfig] = useFrontEndConfig()
  const [userChoice, setUserChoice] = useState(NO_USER_CHOICE)
  const clickedRef = useRef(false)
  // The ids Sun gave to the saved row: the first save goes without them (contract §6.3).
  const idsRef = useRef({})
  const savingRef = useRef(Promise.resolve())

  useEffect(() => {
    if (!enabled) return undefined
    let current = true
    const read = async () => {
      let response = {}
      try {
        response = await apiGet('user/tabletune/' + PREVIEW_TABLE_KEY)
      } catch (error) {
        response = {}
      }
      if (!current) return
      const field = (Array.isArray(response?.fields) ? response.fields : []).find((item) => item?.name === PREVIEW_FIELD_NAME)
      if (field === undefined) return
      idsRef.current = { id: response.id, fieldId: field.id }
      const saved = savedResequencePreviewChoice(response)
      if (saved.chosen && !clickedRef.current) setUserChoice(saved)
    }
    read()
    return () => { current = false }
  }, [enabled])

  const value = resolveResequencePreview(userChoice, frontEndConfig)
  const valueRef = useRef(value)
  valueRef.current = value

  const toggle = useCallback(() => {
    const next = !valueRef.current
    valueRef.current = next
    clickedRef.current = true
    setUserChoice(chooseResequencePreview(next))
    // One save after the other, so the second one carries the ids the first one got.
    savingRef.current = savingRef.current.then(async () => {
      try {
        const response = await apiPost('user/tabletune', resequencePreviewPreferenceBody(next, idsRef.current))
        const saved = Array.isArray(response?.fields) ? response.fields.find((item) => item?.name === PREVIEW_FIELD_NAME) : {}
        idsRef.current = { id: response?.id ?? idsRef.current.id, fieldId: saved?.id ?? idsRef.current.fieldId }
      } catch (error) {
        // POL4: the choice stays for the session and nobody is bothered.
      }
    })
  }, [])

  return [value, toggle]
}

export default useResequencePreviewPreference
